import { z } from 'zod';

/**
 * Fonctionnalités activables client par client (espace /superadmin).
 * Valeur effective = exception du client, sinon valeur par défaut réglée par le superadmin, sinon `defaultEnabled`.
 */
export const FEATURE_CATALOG = {
  ai_assistant: {
    label: 'Assistant IA',
    description: "Analyse de photos par l'IA pour pré-remplir les fiches arbre (menu, page et API).",
    defaultEnabled: true,
  },
  smart_map: {
    label: 'Carte intelligente',
    description: 'Carte avec recherche par zone, cadastre et inventaires (menu et page).',
    defaultEnabled: true,
  },
  print_templates: {
    label: 'Modèles de fiche personnalisés',
    description: "Personnalisation des fiches imprimées de l'organisation (les fiches par défaut restent disponibles).",
    defaultEnabled: true,
  },
} as const;

export type FeatureKey = keyof typeof FEATURE_CATALOG;
export const FEATURE_KEYS = Object.keys(FEATURE_CATALOG) as FeatureKey[];
export type FeatureFlags = Record<FeatureKey, boolean>;

const FeatureKeySchema = z.enum(FEATURE_KEYS as [FeatureKey, ...FeatureKey[]]);

/** Valeurs par défaut pour tous les clients. */
export const FeatureDefaultsSchema = z.partialRecord(FeatureKeySchema, z.boolean());
/** Exceptions d'un client : null = revenir à la valeur par défaut. */
export const FeatureOverridesUpdateSchema = z.partialRecord(FeatureKeySchema, z.boolean().nullable());
export type FeatureDefaults = z.infer<typeof FeatureDefaultsSchema>;
export type FeatureOverridesUpdate = z.infer<typeof FeatureOverridesUpdateSchema>;

/** Calcule les fonctionnalités effectives (les clés inconnues ou mal typées sont ignorées). */
export function resolveFeatureFlags(defaults: unknown, overrides: unknown): FeatureFlags {
  const read = (source: unknown, key: FeatureKey): boolean | undefined => {
    const value = source && typeof source === 'object' ? (source as Record<string, unknown>)[key] : undefined;
    return typeof value === 'boolean' ? value : undefined;
  };
  return Object.fromEntries(
    FEATURE_KEYS.map((key) => [key, read(overrides, key) ?? read(defaults, key) ?? FEATURE_CATALOG[key].defaultEnabled]),
  ) as FeatureFlags;
}
